import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { workExperienceSaga } from 'store/redux-store/candidate-work-experience/saga';
import { selectWorkExperienceView } from 'store/redux-store/candidate-work-experience/selector';
import {
  workExperienceActions,
  workExperienceReducer,
  workExperienceSliceKey,
} from 'store/redux-store/candidate-work-experience/slice';
import { useTranslation } from 'react-i18next';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { getWorkExperienceGridColumns, hasDeleteGridCellAccess, hasEditGridCellAccess } from '../GridUtils';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { WorkExperienceDeleteDialog } from './WorkExperienceDeleteDialog';
import { usePromiseTracker } from 'react-promise-tracker';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import {
  GridColumnVisibilityModel,
  GridPinnedColumns,
  GridRowId,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef,
  GridColumns as XGridColumns,
} from '@mui/x-data-grid-pro';

// Global event system for change detection
const changeDetectionEvents = {
  listeners: new Set<(rowId: GridRowId, updatedRow: GridValidRowModel) => void>(),
  emit(rowId: GridRowId, updatedRow: GridValidRowModel) {
    this.listeners.forEach(listener => listener(rowId, updatedRow));
  },
  subscribe(listener: (rowId: GridRowId, updatedRow: GridValidRowModel) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
};

// Make it available globally for dropdown cells
(window as any).changeDetectionEvents = changeDetectionEvents;
import { WorkExperienceEditConfirmationDialog } from './WorkExperienceEditConfirmationDialog';
import moment from 'moment';
import { brandsSelector, disciplineDataSelector, specialtiesSelector } from 'store/redux-store/lookup/lookup.selectors';
import { WorkExperienceGridStyles } from './gridComponents/WorkExperienceGrid.style';
import { httpSuccess } from 'app/services/serviceHelpers';
import { formatMultiSortColumns, formatMultiSortXGridColumns } from '@AMIEWEB/GlobalSearch/helper';
import _ from 'lodash';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { RootState } from 'src/types/RootState';
import { hasAddModalAccess } from './EditWorkExperience/WorkExperienceUtils';
import { selectUser } from 'oidc/user.selectors';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { ff_candidate_ui_manage_references } from 'app/constants/FeatureFlags/Candidate/Keys';
import { useDecision } from '@optimizely/react-sdk';
import { RequestReferenceModal } from '../../SummaryTab/RequestReferenceModal';
import { defaultPinnedColumns, getCountryOpts, getStateOpts, reasonForLeavingOptions, workExperienceColumnVisibility, userAllowedToEdit } from '../helper';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { AddWorkExperienceWrapper } from './AddWorkExperience/AddWorkExperienceWrapper';
import { candidateEmployementSelection } from '@AMIEWEB/Candidate/CandidateProfile/Profile/CandidateDetails.selector';
import { usePinnedColumnsPreference } from 'app/components/Common/XGrid/Hooks/usePinnedColumnsPreference';

export const WorkExperienceGrid = () => {
  useInjectReducer({ key: workExperienceSliceKey, reducer: workExperienceReducer });
  useInjectSaga({ key: workExperienceSliceKey, saga: workExperienceSaga });

  const dispatch = useDispatch();

  const params = useParams<{
    travelerId: string;
    brandId: string;
  }>();
  const user = useSelector(selectUser);
  const addModalAccess = useMemo(() => hasAddModalAccess(user), [user]);
  const deleteGridCellAccess = useMemo(() => hasDeleteGridCellAccess(user), [user]);
  const editGridCellAccess = useMemo(() => hasEditGridCellAccess(user), [user]);
  const isClinicalUser = useMemo(() => userAllowedToEdit(user?.userInfo), [user]);
  const gridTag = 'CandidateWorkExperience';
  const { t } = useTranslation();
  const { classes } = WorkExperienceGridStyles();
  const [countryOptions] = useState(getCountryOpts());
  const [stateOptions] = useState(getStateOpts());
  const specialtiesLookup = useSelector(specialtiesSelector);
  const disciplines = useSelector(disciplineDataSelector);
  const travelCompanies = useSelector(brandsSelector);
  const userPreference = useSelector(selectUserPreference);

  const [columns, setColumns] = useState<any>(
    getWorkExperienceGridColumns(
      t,
      editGridCellAccess,
      classes,
      disciplines,
      specialtiesLookup,
      travelCompanies,
      reasonForLeavingOptions,
    ),
  );
  const defaultColumns = React.useMemo(() => 
    getWorkExperienceGridColumns(
      t,
      editGridCellAccess,
      classes,
      disciplines,
      specialtiesLookup,
      travelCompanies,
      reasonForLeavingOptions,
    ) as any,
    [t, editGridCellAccess, classes, disciplines, specialtiesLookup, travelCompanies, reasonForLeavingOptions]
  );
  const [open, setOpen] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [hasErrorCells, setHasErrorCells] = useState<boolean>(false);
  const [addWorkExpClickedOnUnsavedRows, setAddWorkExpClickedOnUnsavedRows] = useState<boolean>(false);
  const [requestClickedOnUnsavedRows, setRequestClickedOnUnsavedRows] = useState<boolean>(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = React.useState<any>(
    _.cloneDeep(workExperienceColumnVisibility),
  );
  const [openReferenceRequestPop, setOpenReferenceRequestPop] = useState<boolean>(false);
  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumns>(defaultPinnedColumns);
  const [showAddReferenceRequest] = useDecision(ff_candidate_ui_manage_references);

  const { workExperienceData, editWorkExperienceAPIStatusCode } = useSelector(selectWorkExperienceView);
  const candidateEmploymentDetails = useSelector(candidateEmployementSelection);
  const [isWorkExpDeleteOpen, setWorkExpExpDeleteOpen] = useState(false);
  const { promiseInProgress: deleteWorkExperienceTracker } = usePromiseTracker({
    area: 'delete-work-experience-batch',
    delay: 0,
  });
  const { promiseInProgress: updateWorkExperienceTracker } = usePromiseTracker({
    area: 'update-work-experience-batch',
    delay: 0,
  });
  const { promiseInProgress: getWorkExperienceTracker } = usePromiseTracker({
    area: 'get-candidate-work-experience',
    delay: 0,
  });

  const apiRef = useGridApiRef();
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
  const [isEditConfirmationDialogOpen, setIsEditConfirmationDialogOpen] = useState(false);
  const unsavedChangesRef = useRef<{
    unsavedRows: Record<GridRowId, GridValidRowModel>;
    rowsBeforeChange: Record<GridRowId, GridValidRowModel>;
  }>({
    unsavedRows: {},
    rowsBeforeChange: {},
  });
  const history = useNavigate();

  const gridState = useSelector(selectGridState);
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([]);
  const gridPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === gridTag);
  });

  const workExperiences = React.useMemo(() => {
    let employments = [...workExperienceData?.employment];
    let newEmployments = employments?.map(emp => {
      const isEditAllowed = candidateEmploymentDetails?.find(
        ced => ced?.workHistoryId === emp?.workHistoryId,
      )?.allowEdit;
      return { 
        ...emp, 
        id: emp.workHistoryId, // Ensure id property is preserved for grid
        allowEdit: isEditAllowed,
        verifiedEMPVReferenceCount: emp?.verifiedEMPVReferenceCount || 0,
      };
    });
    return newEmployments;
  }, [candidateEmploymentDetails, workExperienceData?.employment]);

  useEffect(() => {
    if (gridPreferences?.value?.multiSortedColumns?.length > 0) {
      setCurrentSortModel(getSortModelPreferences(gridPreferences?.value?.multiSortedColumns));
    }
  }, [gridPreferences]);

  //Get the grid columns from preferences if available
  const getColumnPreferences = (cols: any[]) => {
    try {
      const columnPreferences = gridPreferences?.value;
      const filterSetColumns = gridPreferences?.value?.filterAttributes?.filterSet?.find(
        item => item.name === gridPreferences?.value?.selectedFilter?.name,
      )?.columns;
      if (columnPreferences && (columnPreferences.columns || filterSetColumns)) {
        const newColumns = cols.map(column => {
          const foundIndex = (filterSetColumns ?? columnPreferences.columns).findIndex(
            item => item?.field === column?.field,
          );
          if (foundIndex !== -1) {
            return {
              ...column,
              ...(filterSetColumns ?? columnPreferences.columns)[foundIndex],
            };
          } else {
            return column;
          }
        });
        return newColumns.sort((a, b) => {
          const filterSetColumnsIndexA = (filterSetColumns ?? columnPreferences.columns).findIndex(
            i => i.field === a.field,
          );
          const filterSetColumnsIndexB = (filterSetColumns ?? columnPreferences.columns).findIndex(
            i => i.field === b.field,
          );

          const indexA = filterSetColumnsIndexA === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexA;
          const indexB = filterSetColumnsIndexB === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexB;

          return indexA - indexB;
        });
      }
      return cols;
    } catch (err) {
      console.error('Error in getColumnPreferences:', err);
      return cols;
    }
  };

  // Custom function to immediately mark a row as having unsaved changes
  const markRowAsChanged = React.useCallback((rowId: GridRowId, updatedRow: GridValidRowModel) => {
    const originalRow = workExperiences.find(row => row.workHistoryId === rowId);
    if (originalRow && !_.isEqual(updatedRow, originalRow)) {
      unsavedChangesRef.current.unsavedRows[rowId] = updatedRow;
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = originalRow;
      }
      setHasUnsavedRows(true);
    }
  }, [workExperiences]);

  // Subscribe to change detection events from dropdown cells
  useEffect(() => {
    const unsubscribe = changeDetectionEvents.subscribe(markRowAsChanged);
    return unsubscribe;
  }, [markRowAsChanged]);

  useEffect(() => {
    loadWorkExperienceData();
    dispatch(workExperienceActions.setSelectedWorkExperience(null));
  }, []);

  const loadWorkExperienceData = () => {
    if (params?.travelerId && params?.brandId) {
      dispatch(
        workExperienceActions.onInitAction({
          candidateId: parseInt(params.travelerId),
          brandId: parseInt(params.brandId),
        }),
      );
    }
  };

  const blocker = useBlocker(hasUnsavedRows);

  useEffect(() => {
    if (hasUnsavedRows && blocker.state === "blocked") {
      // Small delay to ensure state is fully updated
      setTimeout(() => {
        setIsEditConfirmationDialogOpen(true);
        setNextLocation(blocker.location);
      }, 10);
    }
  }, [blocker, hasUnsavedRows]);

  useEffect(() => {
    if (editWorkExperienceAPIStatusCode && httpSuccess(editWorkExperienceAPIStatusCode)) {
      setHasUnsavedRows(false);
      unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {},
      };
      dispatch(workExperienceActions.setEditWorkExperienceAPIStatusCode(null));
    }
  }, [editWorkExperienceAPIStatusCode]);

  // Ensure state consistency between hasUnsavedRows and actual unsaved changes
  useEffect(() => {
    const hasActualChanges = Object.keys(unsavedChangesRef.current.unsavedRows).length > 0;
    if (hasUnsavedRows !== hasActualChanges) {
      setHasUnsavedRows(hasActualChanges);
    }
  }, [hasUnsavedRows]);

  const resetColumns = () => {
    //Reset grid attributes to default values
    setColumns(
      getWorkExperienceGridColumns(
        t,
        editGridCellAccess,
        classes,
        disciplines,
        specialtiesLookup,
        travelCompanies,
        reasonForLeavingOptions,
      ),
    );
    setCurrentSortModel([]);
    setCurrentColumnVisibilityModel(workExperienceColumnVisibility);
    setPinnedColumns(defaultPinnedColumns);

    const preferenceData = {
      id: gridTag,
      value: {
        ...(gridPreferences?.value || {}),
        columns: defaultColumns,
        columnVisibilityModel: workExperienceColumnVisibility,
        pinnedColumns: defaultPinnedColumns,
        multiSortedColumns:[]
      },
    };
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    dispatch(
      globalActions.setSnackBar({
        message: 'Columns have been reset.',
        severity: 'success',
      }),
    );
  };

  const handleNavigation = () => {
    if (hasUnsavedRows) {
      setAddWorkExpClickedOnUnsavedRows(true);
      setIsEditConfirmationDialogOpen(true);
    } else {
      setAddWorkExpClickedOnUnsavedRows(false);
      setOpen(true);
    }
  };

  const handleRequestNavigation = () => {
    if (hasUnsavedRows) {
      setRequestClickedOnUnsavedRows(true);
      setIsEditConfirmationDialogOpen(true);
    } else {
      setRequestClickedOnUnsavedRows(false);
      setOpenReferenceRequestPop(true);
    }
  };

  const handleClose = () => {
    setAddWorkExpClickedOnUnsavedRows(false);
    setOpen(false);
    setOpenReferenceRequestPop(false);
    setOpenReferenceRequestPop(false);
  };

  const handleRefreshGrid = () => {
    if (hasUnsavedRows) {
      setIsEditConfirmationDialogOpen(true);
    } else {
      loadWorkExperienceData();
    }
  };

  const handleOnEditConfirmationProceed = () => {
    setHasUnsavedRows(false);
    apiRef.current.updateRows(Object.values(unsavedChangesRef.current.rowsBeforeChange));
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    };
    setIsEditConfirmationDialogOpen(false);

    if (nextLocation) {
      setTimeout(() => {
        history(`${nextLocation?.pathname}${nextLocation?.search}`); //Delaying this call to make sure the hasUnsavedRows value is reset before unblocking the history.
      }, 0);
    } else if (addWorkExpClickedOnUnsavedRows) {
      setOpen(true);
    } else if (requestClickedOnUnsavedRows) {
      setOpenReferenceRequestPop(true);
    }
  };

  const handleOnEditConfirmationCancel = () => {
    setNextLocation(null);
    setAddWorkExpClickedOnUnsavedRows(false);
    setIsEditConfirmationDialogOpen(false);
    setRequestClickedOnUnsavedRows(false);
    setOpenReferenceRequestPop(false);
  };

  const handleSaveChanges = async () => {
    try {
      const unSavedRows = Object.values(unsavedChangesRef.current.unsavedRows);
      let workExperienceList = [];
      unSavedRows.forEach(row => {
        const selectedCountry = countryOptions.find(e => e.label == row?.country);
        const selectedState = stateOptions.find(e => e.label == row?.state);
        const selectedDiscipline = disciplines?.find(x => x?.object?.Abbreviation === row?.disciplineAbbr);
        const selectedSpecialty = specialtiesLookup?.find(x => x.label === row?.specialtyAbbr);
        const selectedTravelCompany = travelCompanies?.find(x => x.name === row?.travelCompany);

        let workExperienceRow = {
          ...row,
          workHistoryId: row.workHistoryId,
          startedOn: row.startedOn ? moment(row.startedOn).format('yyyy-MM-DDT00:00:00') : null,
          endedOn: row.endedOn ? moment(row.endedOn).format('yyyy-MM-DDT00:00:00') : null,
          workGap: 0,
          previousEndedOn: null,
          yearsOfExperience: 0,
          monthsOfExperience: 0,
          disciplineId: selectedDiscipline?.object?.ID || null,
          discipline: selectedDiscipline?.object?.Description || '',
          disciplineAbbr: selectedDiscipline?.object?.Abbreviation || '',
          specialtyId: selectedSpecialty?.object?.SpecialtyID || null,
          specialty: selectedSpecialty?.object?.Description || '',
          specialtyAbbr: selectedSpecialty?.object?.Description || '',
          position: row.position,
          approvedReferenceCount: row.approvedReferenceCount,
          facilityName: row.facilityName,
          location: {
            address1: row.location.address1,
            address2: row.location.address2,
            city: row.city,
            stateProvinceID: selectedState ? selectedState.id : row.state,
            state: selectedState ? selectedState.id : row.state,
            country: row.country,
            zip: row.zip,
            countryId: selectedCountry?.id,
            latitude: row.location.latitude,
            longitude: row.location.longitude,
          },
          unit: row.unit,
          staffingAgency: row.staffingAgency ? 'Y' : 'N',
          isTeachingHospital: row.isTeachingHospital ? 'Y' : 'N',
          numberOfBedsInFacility: row.numberOfBedsInFacility,
          currentlyEmployed: row.currentlyEmployed,
          placementID: null,
          reasonForLeaving: row.reasonForLeaving,
          timeStamp: row.timeStamp,
          ref: null,
          otherDiscipline: '',
          otherSpecialty: '',
          equipmentProcedures: row.equipmentProcedures,
          isSupervisor: row.isSupervisor,
          isTeletherapy: row.isTeletherapy,
          travelCompany: selectedTravelCompany?.name || null,
          travelCompanyId: selectedTravelCompany?.value || null,
          verifiedEMPVReferenceCount: row?.verifiedEMPVReferenceCount || 0,
        };

        workExperienceList.push(workExperienceRow);
      });

      dispatch(
        workExperienceActions.updateWorkExperienceBulk({
          workExperienceList: workExperienceList,
        }),
      );
    } catch (error) {}
  };

  const processRowUpdate = React.useCallback((newRow, oldRow) => {
    // Immediate change detection without setTimeout
    const hasChanges = Object.keys(newRow).some(key => {
      const newValue = newRow[key];
      const oldValue = oldRow[key];
      
      // Handle date comparisons
      if (newValue instanceof Date && oldValue instanceof Date) {
        return newValue.getTime() !== oldValue.getTime();
      }
      
      // Handle null/undefined differences
      if (newValue === null && oldValue === undefined) return true;
      if (newValue === undefined && oldValue === null) return true;
      
      // Handle string comparisons (common for dropdowns)
      if (typeof newValue === 'string' && typeof oldValue === 'string') {
        return newValue !== oldValue;
      }
      
      // Handle boolean comparisons
      if (typeof newValue === 'boolean' && typeof oldValue === 'boolean') {
        return newValue !== oldValue;
      }
      
      // Handle number comparisons
      if (typeof newValue === 'number' && typeof oldValue === 'number') {
        return newValue !== oldValue;
      }
      
      return !_.isEqual(newValue, oldValue);
    });
    
    if (!hasChanges) {
      // Only clear if there are no changes in any row
      const hasAnyChanges = Object.keys(unsavedChangesRef.current.unsavedRows).length > 0;
      if (!hasAnyChanges) {
        setHasUnsavedRows(false);
      }
      return newRow;
    }
    
    const rowId = newRow.workHistoryId;
    
    // Immediately update the unsaved changes
    unsavedChangesRef.current.unsavedRows[rowId] = newRow;
    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
      unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }
    
    // Immediately set hasUnsavedRows to true
    setHasUnsavedRows(true);
    
    // Check for error cells after a brief delay to allow DOM update
    setTimeout(() => {
      const errorCellElements = document.getElementsByClassName(classes.isErrorCell);
      setHasErrorCells(errorCellElements?.length ? true : false);
    }, 50);
    
    return newRow;
  }, [classes.isErrorCell]);

  const leftActions = React.useMemo<IXGridBulkActionButton[]>(
    () => [
      // @ts-ignore
      {
        title: t('candidate.workExperience.workExperienceGrid.saveChanges'),
        Icon: CheckIcon,
        disabled: !hasUnsavedRows || hasErrorCells,
        onClick: handleSaveChanges,
      },
      // @ts-ignore
      {
        title: t('candidate.workExperience.workExperienceGrid.cancelChanges'),
        Icon: ClearIcon,
        disabled: !hasUnsavedRows,
        onClick: event => {
          setIsEditConfirmationDialogOpen(true);
        },
      },
      // @ts-ignore
      {
        title: t('candidate.workExperience.workExperienceGrid.addWorkExperienceAction'),
        Icon: AddIcon,
        disabled: !addModalAccess,
        tooltipProps: !addModalAccess && { tooltipContent: t('candidate.workExperienceView.permissionDisabled') },
        onClick: handleNavigation,
      },
      // @ts-ignore
      ...(showAddReferenceRequest?.enabled
        ? [
            {
              title: t('Request'),
              Icon: PersonAddOutlinedIcon,
              disabled: false,
              onClick: handleRequestNavigation,
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUnsavedRows, hasErrorCells, isEditConfirmationDialogOpen],
  );

  useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(gridTag));
      determineVisibilityModel();
      determineColumn();
      determinePinnedColumns();
    }
  }, [userPreference?.loaded, gridPreferences]);

  const getSortModelPreferences = (sortModel: any[]) => {
    const columnPreferences = gridPreferences?.value;
    if (
      columnPreferences &&
      columnPreferences?.multiSortedColumns &&
      Array.isArray(columnPreferences?.multiSortedColumns)
    ) {
      return formatMultiSortXGridColumns(columnPreferences?.multiSortedColumns);
    }
    return sortModel;
  };

  const getVisibilityPreferences = (cols: any) => {
    const columnPreferences = gridPreferences?.value;
    if (columnPreferences && columnPreferences?.columnVisibilityModel) {
      return columnPreferences?.columnVisibilityModel;
    }
    return cols;
  };
    // When user pins/unpins a column, update & save
  const handlePinnedColumnsChange = usePinnedColumnsPreference({
    gridTag,
    gridPreferences,
    setPinnedColumns,
  });

  const determineVisibilityModel = () => {
    const currentVisibilityModel = getVisibilityPreferences(workExperienceColumnVisibility);
    setCurrentColumnVisibilityModel(currentVisibilityModel);
  };
  const determineColumn = () => {
    const currentColumn = getColumnPreferences(columns);
    setColumns(currentColumn);
  };
  const determinePinnedColumns = () => {
    const pinnedColumn = gridPreferences?.value?.pinnedColumns || defaultPinnedColumns;
    setPinnedColumns(pinnedColumn);
  };

  const handleWorkExpDelete = () => {
    if (selectedIds?.length) {
      dispatch(
        workExperienceActions.deleteWorkExperienceBatchAction({
          workExperienceList: workExperienceData?.employment,
          workHistoryIds: selectedIds,
        }),
      );
      setWorkExpExpDeleteOpen(false);
    }
  };

  const rightActions = React.useMemo(() => {
    return [
      {
        key: 'deleteWorkExperience',
        Icon: DeleteOutlinedIcon,
        disabled: !selectedIds?.length || !deleteGridCellAccess,
        onClick: () => (deleteGridCellAccess ? setWorkExpExpDeleteOpen(true) : null),
        tooltipProps: {
          tooltipContent: deleteGridCellAccess ? 'Delete' : t('candidate.workExperienceView.permissionDisabled'),
        },
      },
    ];
  }, [selectedIds]);

  const onSortModelChanged = (model: GridSortModel) => {
    saveSortModel(model);
    setCurrentSortModel(model);
  };

  const saveSortModel = React.useCallback(
    sortModel => {
      const multiSortedColumns = sortModel?.length > 0 ? formatMultiSortColumns(sortModel) : undefined;
      if (!_.isEqual(multiSortedColumns, gridState?.multiSortedColumns)) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            multiSortedColumns: multiSortedColumns,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridPreferences?.value, defaultColumns?.length, gridState?.multiSortedColumns, currentColumnVisibilityModel],
  );

  const savePreferences = React.useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      field?: string;
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!columns || columns.length === 0 || !userPreference.loaded) return;
      const currentGridColumns = new GridColumns(gridColumns).columns;

      // Remove the automatic checkbox column (has no field name)
      if (currentGridColumns && currentGridColumns.length > 0 && !currentGridColumns[0].field)
        currentGridColumns.shift();

      const comparableColumns = new GridColumns(
        gridPreferences?.value?.columns,
        gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    [userPreference.loaded, gridPreferences?.value, dispatch],
  );

  const GridComponent = React.useMemo(() => {
    return (
      <XGrid
        components={{
          NoRowsOverlay: () => {
            return (
              <div className="center" style={{ height: '100%' }}>
                {t('candidate.workExperience.workExperienceGrid.noRecordsFound')}
              </div>
            );
          },
        }}
        getRowId={row => row.id}
        gridTag={gridTag}
        apiRef={apiRef}
        experimentalFeatures={{ columnGrouping: true, newEditingApi: true }}
        loading={deleteWorkExperienceTracker || updateWorkExperienceTracker || getWorkExperienceTracker}
        rows={workExperiences}
        isRowSelectable={params => isClinicalUser || !params.row?.isDisabled}
        isCellEditable={params => editGridCellAccess}
        defaultColumns={defaultColumns}
        columns={columns}
        disableSelectionOnClick
        checkboxSelection={true}
        pagination={false}
        height={490}
        sx={{
          minWidth: '500px',
          '& .MuiDataGrid-row.row--edited': {
            backgroundColor: () => {
              return 'rgba(0, 111, 185, 0.08)';
            },
          },
        }}
        hideFooter
        inDialog
        title={t('candidate.workExperience.workExperienceGrid.gridTitle')}
        displayCustomCount={workExperienceData?.employment?.length}
        editMode="row"
        onSelectionModelChange={ids => setSelectedIds(ids)}
        selectionModel={selectedIds}
        onColumnResizeStop={_columns => {
          savePreferences({
            field: undefined,
            columns: _columns,
            visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? workExperienceColumnVisibility),
          });
        }}
        onColumnOrderChange={(params, event, details, _columns) => {
          savePreferences({
            field: params.field,
            columns: _columns,
            visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? workExperienceColumnVisibility),
          });
        }}
        onColumnVisibilityModelChange={(model, details) => {
          dispatch(gridStateActions.setColumnVisibilityModel(model));
          setCurrentColumnVisibilityModel(_.cloneDeep(model));
          savePreferences({
            field: undefined,
            columns: _.cloneDeep(apiRef?.current?.getAllColumns()),
            visibilityModel: _.cloneDeep(model),
          });
        }}
        toolbarProps={{
          refreshButton: true,
          resetButton: true,
          leftActions: leftActions,
          rightActions: rightActions,
          resetColumns: resetColumns,
          refreshGrid: handleRefreshGrid,
        }}
        processRowUpdate={(updatedRow, originalRow) => processRowUpdate(updatedRow, originalRow)}
        getRowClassName={({ id }) => {
          // Find the row by grid id and get its workHistoryId
          const row = workExperiences.find(r => r.id === id);
          const rowId = row?.workHistoryId;
          const unsavedRow = rowId ? unsavedChangesRef.current.unsavedRows[rowId] : null;
          if (unsavedRow) {
            return 'row--edited';
          }
          return '';
        }}
        pinnedColumns={pinnedColumns}                    
        onPinnedColumnsChange={handlePinnedColumnsChange}
        disableMultipleColumnsSorting={false}
        defaultSortModel={currentSortModel}
        sortModel={currentSortModel}
        onSortModelChange={onSortModelChanged}
        defaultColumnVisibilityModel={workExperienceColumnVisibility}
        columnVisibilityModel={currentColumnVisibilityModel}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workExperienceData,
    columns,
    selectedIds,
    deleteWorkExperienceTracker,
    updateWorkExperienceTracker,
    processRowUpdate,
    editGridCellAccess,
    isClinicalUser,
    getWorkExperienceTracker,
    handleRefreshGrid,
  ]);

  // Override the updateRows method to detect changes from dropdown cells
  useEffect(() => {
    if (apiRef.current) {
      const originalUpdateRows = apiRef.current.updateRows;
      apiRef.current.updateRows = (updates) => {     
        // Call the original method
        const result = originalUpdateRows.call(apiRef.current, updates);
        
        // Check for changes and trigger our detection logic
        if (updates && updates.length > 0) {
          updates.forEach(updatedRow => {
            const originalRow = workExperiences.find(row => row.workHistoryId === updatedRow.workHistoryId);
            if (originalRow && !_.isEqual(updatedRow, originalRow)) {
              // Trigger change detection for this row using workHistoryId as the key
              const rowId = updatedRow.workHistoryId;
              unsavedChangesRef.current.unsavedRows[rowId] = updatedRow;
              if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
                unsavedChangesRef.current.rowsBeforeChange[rowId] = originalRow;
              }
              setHasUnsavedRows(true);
            }
          });
        }
        
        return result;
      };
      
      // Cleanup function to restore original method
      return () => {
        if (apiRef.current) {
          apiRef.current.updateRows = originalUpdateRows;
        }
      };
    }
  }, [workExperiences, apiRef]);

  // Listen for cell editing events to detect changes immediately
  useEffect(() => {
    if (apiRef.current) {
      const originalSetEditCellValue = apiRef.current.setEditCellValue;
      
      apiRef.current.setEditCellValue = (params: any, event?: any) => {   
        // Call the original method
        const result = originalSetEditCellValue.call(apiRef.current, params, event);
        
        // Check for changes immediately after setting the cell value
        setTimeout(() => {
          const currentRow = apiRef.current.getRow(params.id);
          if (currentRow) {
            const originalRow = workExperiences.find(row => row.workHistoryId === params.id);
            if (originalRow) {
              // Check if the specific field has changed
              const newValue = currentRow[params.field];
              const oldValue = originalRow[params.field];
              
              let hasFieldChange = false;
              
              // Handle different data types
              if (newValue instanceof Date && oldValue instanceof Date) {
                hasFieldChange = newValue.getTime() !== oldValue.getTime();
              } else if (newValue === null && oldValue === undefined) {
                hasFieldChange = true;
              } else if (newValue === undefined && oldValue === null) {
                hasFieldChange = true;
              } else if (typeof newValue === 'string' && typeof oldValue === 'string') {
                hasFieldChange = newValue !== oldValue;
              } else if (typeof newValue === 'boolean' && typeof oldValue === 'boolean') {
                hasFieldChange = newValue !== oldValue;
              } else if (typeof newValue === 'number' && typeof oldValue === 'number') {
                hasFieldChange = newValue !== oldValue;
              } else {
                hasFieldChange = !_.isEqual(newValue, oldValue);
              }
              
              if (hasFieldChange) {
                const rowId = params.id;
                unsavedChangesRef.current.unsavedRows[rowId] = currentRow;
                if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
                  unsavedChangesRef.current.rowsBeforeChange[rowId] = originalRow;
                }
                setHasUnsavedRows(true);
              }
            }
          }
        }, 0);
        
        return result;
      };
      
      // Cleanup function to restore original methods
      return () => {
        if (apiRef.current) {
          apiRef.current.setEditCellValue = originalSetEditCellValue;
        }
      };
    }
  }, [workExperiences, apiRef]);

  return (
    <LayoutGrid>
      <LayoutGridItem xs={12}>{GridComponent}</LayoutGridItem>
      {open && (
        <AddWorkExperienceWrapper
          open={open}
          handleClose={handleClose}
          addType={t('candidate.workExperience.workExperienceGrid.workType')}
        />
      )}
      {isWorkExpDeleteOpen && selectedIds?.length && (
        <WorkExperienceDeleteDialog
          open={isWorkExpDeleteOpen}
          setDialogOpen={setWorkExpExpDeleteOpen}
          handleProceed={handleWorkExpDelete}
        />
      )}
      {hasUnsavedRows && isEditConfirmationDialogOpen && (
        <WorkExperienceEditConfirmationDialog
          open={hasUnsavedRows && isEditConfirmationDialogOpen}
          setDialogOpen={handleOnEditConfirmationCancel}
          handleProceed={handleOnEditConfirmationProceed}
        />
      )}
      {openReferenceRequestPop && (
        <RequestReferenceModal open={openReferenceRequestPop} handleClose={() => setOpenReferenceRequestPop(false)} />
      )}
    </LayoutGrid>
  );
};
